/**
 * Line Item Explorer — the pure half: query building, keyword presets,
 * totals and CSV. The page (`LineItemExplorerPage`) only wires these to
 * state; the matching itself is server-side (sm4rt-api
 * `invoice_line_item_search_service`), so the rules here only shape the
 * request and summarise the response.
 */
import type {
  InvoiceLineItemSearchRow,
  LineItemMatchField,
  LineItemMatchMode,
} from '@techgitter/contracts';

export interface LineItemExplorerFilters {
  /** Dataverse customer GUID; null = all customers. */
  customerId: string | null;
  /** YYYY-MM-DD, or '' for open-ended. */
  from: string;
  to: string;
  /** Comma-separated terms, as typed. */
  keywords: string;
  matchIn: LineItemMatchField;
  mode: LineItemMatchMode;
}

export interface KeywordPreset {
  key: string;
  label: string;
  /** A trailing `*` anchors the term to the start of the field. */
  terms: string[];
}

/** Code-defined presets: one click fills the keyword field (any mode). */
export const KEYWORD_PRESETS: KeywordPreset[] = [
  { key: 'domains', label: 'Domain lines', terms: ['Hosting.Domain*', 'BDomain*', 'domain'] },
  { key: 'hosting', label: 'Web hosting', terms: ['Hosting.Web*'] },
  { key: 'm365', label: 'Microsoft 365', terms: ['BML.M365*', 'Microsoft 365'] },
  { key: 'remote', label: 'Remote support', terms: ['RSupport.Remote*', 'BSupport.Remote*'] },
];

/** Comma-separated terms with blanks (and lone `*`) dropped. */
export function parseKeywords(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.replace(/\*+$/, '').trim() !== '');
}

export function presetKeywords(preset: KeywordPreset): string {
  return preset.terms.join(', ');
}

/** The preset the keyword field currently equals, if any — lights its chip. */
export function activePresetKey(keywords: string): string | null {
  const typed = parseKeywords(keywords).join('\u0000').toLowerCase();
  const hit = KEYWORD_PRESETS.find((p) => p.terms.join('\u0000').toLowerCase() === typed);
  return hit ? hit.key : null;
}

/**
 * Why the search can't run yet, or null when it can. Mirrors the API's 400s
 * so the button is disabled instead of erroring.
 */
export function searchBlockedReason(f: LineItemExplorerFilters): string | null {
  if (!f.customerId && parseKeywords(f.keywords).length === 0) {
    return 'Enter a keyword to search all customers, or pick a customer.';
  }
  if (f.from && f.to && f.from > f.to) {
    return '"From" must be on or before "To".';
  }
  return null;
}

export function buildSearchParams(f: LineItemExplorerFilters): URLSearchParams {
  const qs = new URLSearchParams();
  if (f.customerId) qs.set('customerId', f.customerId);
  if (f.from) qs.set('from', f.from);
  if (f.to) qs.set('to', f.to);
  const terms = parseKeywords(f.keywords);
  if (terms.length) qs.set('q', terms.join(','));
  if (f.matchIn !== 'both') qs.set('matchIn', f.matchIn);
  if (f.mode !== 'any') qs.set('mode', f.mode);
  return qs;
}

export interface LineItemTotals {
  lines: number;
  invoices: number;
  total: number;
  paid: number;
  unpaid: number;
}

/** Sum of line totals, split by the invoice's paid flag (cents-safe). */
export function computeTotals(rows: InvoiceLineItemSearchRow[]): LineItemTotals {
  let paidCents = 0;
  let unpaidCents = 0;
  const invoices = new Set<string>();
  for (const r of rows) {
    const cents = Math.round((Number(r.lineTotal) || 0) * 100);
    if (r.isPaid) paidCents += cents;
    else unpaidCents += cents;
    invoices.add(r.invoiceId || r.invoiceNumber);
  }
  return {
    lines: rows.length,
    invoices: invoices.size,
    total: (paidCents + unpaidCents) / 100,
    paid: paidCents / 100,
    unpaid: unpaidCents / 100,
  };
}

const CSV_HEADER = [
  'Invoice', 'Invoice date', 'Customer', 'Product code', 'Description',
  'Quantity', 'Unit price', 'Line total', 'Paid', 'RS invoice id',
];

/**
 * Quote a CSV field. A leading = + - @ (or tab/CR) is prefixed with `'` so a
 * spreadsheet never evaluates customer-facing text as a formula.
 */
export function csvField(value: string | number | boolean | null | undefined): string {
  let s = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: InvoiceLineItemSearchRow[]): string {
  const lines = [CSV_HEADER.map(csvField).join(',')];
  for (const r of rows) {
    lines.push([
      r.invoiceNumber,
      r.invoiceDate ?? '',
      r.customerName,
      r.productCode,
      r.description,
      r.quantity,
      r.unitPrice.toFixed(2),
      r.lineTotal.toFixed(2),
      r.isPaid ? 'Yes' : 'No',
      r.rsInvoiceId ?? '',
    ].map(csvField).join(','));
  }
  // CRLF + BOM: what Excel expects, so the AU dates and any non-ASCII
  // description open cleanly.
  return '﻿' + lines.join('\r\n') + '\r\n';
}

/** e.g. `line-items_2025-01-01_to_2026-10-30.csv`. */
export function csvFilename(f: LineItemExplorerFilters, today: string): string {
  return `line-items_${f.from || 'start'}_to_${f.to || today}.csv`;
}
